/**
 * “Use this account to buy” prompt.
 *
 * When a product is added to the cart **from the community** (not from inside a
 * shop) the visitor usually has no account in that shop yet. This context asks
 * them to continue with Google for **that shop**: the API finds or creates the
 * shop's customer from the verified Google email (see `_customer_session` in
 * `Backend_API/routers/social_auth.py`), so the owner sees a real customer in
 * their dashboard and the order is linked to an account. The cart is kept — it
 * lives in localStorage.
 *
 * Rendered once by `CommunityLayout` (`BuyAccountModal`).
 */
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useCustomer } from './CustomerContext';

const BuyContext = createContext(null);

export function BuyProvider({ children }) {
  const { customer, isLoggedIn } = useCustomer();
  const [ask, setAsk] = useState(null);          // { shop: {...} }

  /**
   * Ask for a shop account — returns `false` when the visitor is already a
   * customer of that shop (nothing to ask), `true` when the modal opened.
   */
  const askBuy = useCallback((shop) => {
    if (!shop?.id) return false;
    if (isLoggedIn && customer?.shop_id === shop.id) return false;
    setAsk({ shop });
    return true;
  }, [customer, isLoggedIn]);

  const value = useMemo(() => ({
    ask,
    askBuy,
    closeAsk: () => setAsk(null),
  }), [ask, askBuy]);

  return <BuyContext.Provider value={value}>{children}</BuyContext.Provider>;
}

export const useBuy = () => useContext(BuyContext) || {};
